import { historySid } from './historySession';

// The first in-memory route. MemoryRouter never writes the address bar, so
// the only way a link from outside the app reaches its page (the emailed
// /reset-password?token=...) is to start the router at the address the
// browser loaded. Every navigation after that stays in memory, as before.
export function bootEntry(loc: { pathname: string; search: string } = window.location): string {
  const path = loc.pathname && loc.pathname.startsWith('/') ? loc.pathname : '/';
  return path + (loc.search || '');
}

// Reads the boot entry once, then puts the address bar back to "/" (as it reads
// everywhere else in the app). A one-time link such as the reset token would
// otherwise sit in the address bar and history, and a refresh would reopen
// the form with a spent token.
let consumed: string | null = null;
// The in-memory route the user is on now (path + search). consumeBootUrl sets
// it to the boot entry (MemoryRouter starts exactly there), and
// BootRouteTracker moves it on with each navigation.
let current: string | null = null;

// D1: HistoryBridge keeps the in-app page in history.state.memPath while the
// address bar reads "/". A reload (F5, a service-worker reload, Back into the
// app from another site) lands on "/" with that state: start there. A pasted
// /path (or any query) wins: it is a fresh link.
// Only an entry of this tab's own session (sid, historySession.ts) counts: the
// previous user's or company's pages are never reopened.
export function restoredEntry(
  loc: { pathname: string; search: string } = window.location,
  state: unknown = window.history.state,
  sid: string | null = historySid(),
): string | null {
  const s = state && typeof state === 'object' ? (state as { memPath?: unknown; sid?: unknown }) : undefined;
  const mem = s?.memPath;
  const own = !!sid && s?.sid === sid;
  return own && loc.pathname === '/' && !loc.search && typeof mem === 'string' && mem.startsWith('/') ? mem : null;
}

export function consumeBootUrl(): string {
  const entry = restoredEntry() ?? bootEntry();
  consumed = entry;
  current = entry;
  const loc = window.location;
  if (loc.pathname !== '/' || loc.search || loc.hash) {
    window.history.replaceState(window.history.state, '', '/');
  }
  return entry;
}

// The path this tab was loaded at, which the address bar no longer shows once
// consumeBootUrl has run (before it runs, the address bar's own path).
export function loadedPath(): string {
  const url = consumed ?? bootEntry();
  return url.split(/[?#]/)[0] || '/';
}

export function trackRoute(pathWithSearch: string | null): void {
  current = pathWithSearch;
}

// A finished one-time flow (a password reset that succeeded): its boot address
// must never be put back, even while the page still shows it.
export function forgetBootUrl(): void {
  consumed = null;
}

// Puts the consumed address back before the page reloads itself (a new
// service worker taking over right after a deploy), so the reload starts at
// the emailed reset link again instead of "/" (and the sign-in screen). Only
// while the user is still on that boot route: once they have moved on (signed
// in after /login?reason=idle, say), the reload starts at "/" as before.
export function restoreBootUrl(): void {
  if (consumed && consumed !== '/' && current === consumed) {
    window.history.replaceState(window.history.state, '', consumed);
  }
}
